import type {
  GenericViewColumnV2,
  GenericViewControlValueType,
  IGenericViewDefinitionV2,
  IGenericViewToolbarControlV2,
} from '@featit/shared';
import type {
  DataColumnFilterOperator,
  IDataColumnFilterValue,
} from '@/components/DataColumnFilterDialog';
import type { DataColumnSortDirection } from '@/components/DataColumnHeader';

/**
 * URL convention for a configured view — the same split the work-items dashboard uses
 * (see `features/work-items/useWorkItemUrlSync.ts`).
 *
 *   path  = WHAT you are looking at   /acme/featit/-/views/roadmap
 *   query = HOW you are looking at it ?c.layout=board&sort=title:asc
 *
 * React state stays the source of truth. The URL seeds it on cold load and mirrors it
 * afterwards, so a refresh, a copied link and the back button all land on one state.
 *
 * Params written here; anything else in the query belongs to the hosting page and is
 * left untouched:
 *   c.<controlId>  one toolbar control value, omitted while it equals its default
 *   sort           `<columnId>:asc|desc` for the reader's column sort
 *   f.<columnId>   `<operator>:<value>` for one column filter
 *   drill          `<datasetId>|<dimension>:<member>,...|<label>` for the open drill cell
 *
 * Everything decodes defensively: a param naming a control, column or dataset the view
 * no longer has is dropped, never thrown on.
 */

/**
 * Owned by the hosting page rather than the runtime: the work item open in the detail
 * panel, and the work area it came from when a federated view mixes several.
 */
export const SELECTED_ITEM_PARAM = 'item';
export const SELECTED_ITEM_AREA_PARAM = 'itemArea';

/**
 * F-0363: the sidebar's own "New view" has no draft state to start one from — only this page
 * does, once its metadata has loaded — so it asks for a draft the same way a deep link asks for
 * an item, through the address rather than a cross-component callback. It stays in the address for
 * as long as the draft is open (F-0549 VO-11): the bare Views address without it is the overview, so
 * the draft's editor is `…/views?new=1`, and Save or Cancel is what takes it out. A reader who comes
 * back to a stale link with this still in it just gets another fresh draft, never a blank page.
 */
export const NEW_VIEW_PARAM = 'new';

/**
 * F-0549 VO-11: `…/views/:viewId?edit=1` is that view's editor, and the editor is the whole page.
 * Edit pushes it, so Back leaves the editor (through the unsaved-changes guard); Cancel and Save
 * take it out again. A tile's ⋯ → Edit links straight to it.
 */
export const EDIT_VIEW_PARAM = 'edit';

/**
 * F-0549 VO-10: the `location.state` key a view page's "Views" link carries back to the overview,
 * holding the open view's url id, so the overview can put focus back on the tile that opened it.
 */
export const VIEWS_OVERVIEW_FOCUS_STATE = 'viewsOverviewFocusViewId';

/**
 * F-0549 VO-19: under All work areas and a namespace a view's address names no work area of its
 * own, so the overview names it here (`…/views/:viewId?area=<work area id>`). The view page reads
 * only this: never the work area last selected elsewhere in the app. Not a view parameter
 * (`isViewUrlParam`), so the view's own reset of its query keeps it.
 */
export const VIEW_WORK_AREA_PARAM = 'area';

const CONTROL_PREFIX = 'c.';
const FILTER_PREFIX = 'f.';
const SORT_PARAM = 'sort';
const DRILL_PARAM = 'drill';

const FILTER_OPERATORS: readonly DataColumnFilterOperator[] = [
  'contains',
  'equals',
  'not-equals',
  'greater-than',
  'less-than',
  'after',
  'before',
  'on-or-after',
  'on-or-before',
];

export interface IRuntimeColumnSort {
  columnId: string;
  datasetId: string;
  fieldId: string;
  direction: DataColumnSortDirection;
}

export interface IRuntimeColumnFilter extends IDataColumnFilterValue {
  columnId: string;
  datasetId: string;
  fieldId: string;
}

export interface IViewUrlDrill {
  sourceDatasetId: string;
  coordinates: Record<string, string>;
  label: string;
}

/** The slice of a view's runtime state that survives a reload. */
export interface IViewUrlState {
  controls: Record<string, GenericViewControlValueType>;
  sort: IRuntimeColumnSort | null;
  filters: Record<string, IRuntimeColumnFilter>;
  drill: IViewUrlDrill | null;
}

export const EMPTY_VIEW_URL_STATE: IViewUrlState = {
  controls: {},
  sort: null,
  filters: {},
  drill: null,
};

export function toolbarDefaultValues(
  definition: IGenericViewDefinitionV2,
): Record<string, GenericViewControlValueType> {
  return Object.fromEntries(
    definition.toolbar
      .filter((control) => control.defaultValue !== undefined)
      .map((control) => [control.id, control.defaultValue!]),
  );
}

/** Toolbar defaults with the URL's overrides on top — what the runtime starts from. */
export function viewStateFromUrl(
  definition: IGenericViewDefinitionV2,
  urlState: IViewUrlState,
): IViewUrlState {
  return {
    controls: { ...toolbarDefaultValues(definition), ...urlState.controls },
    sort: urlState.sort,
    filters: urlState.filters,
    drill: urlState.drill,
  };
}

/**
 * Whether the URL carried any reader state this view recognises.
 *
 * The precedence question, not a formatting one: a link the reader followed must show what its
 * sender saw, and anything they remembered from last time must not be layered underneath it. But
 * "the query string is non-empty" is the wrong test — the hosting page has params of its own, and
 * switching views inside one page leaves the previous view's `c.` params in the address, where they
 * name controls this definition does not have. Both cases would read as a link and silently discard
 * what the reader left. Ask the decoded state instead: it has already dropped everything this
 * definition cannot place.
 */
export function hasViewUrlState(state: IViewUrlState): boolean {
  return (
    Object.keys(state.controls).length > 0 ||
    Object.keys(state.filters).length > 0 ||
    state.sort !== null ||
    state.drill !== null
  );
}

export function readViewUrlState(
  search: string | URLSearchParams,
  definition: IGenericViewDefinitionV2,
): IViewUrlState {
  const params = searchParams(search);
  const controls: Record<string, GenericViewControlValueType> = {};
  const filters: Record<string, IRuntimeColumnFilter> = {};
  let sort: IRuntimeColumnSort | null = null;
  let drill: IViewUrlDrill | null = null;

  for (const [key, raw] of params.entries()) {
    if (key.startsWith(CONTROL_PREFIX)) {
      const control = definition.toolbar.find(
        (candidate) => candidate.id === key.slice(CONTROL_PREFIX.length),
      );
      const value = control ? decodeControlValue(control, raw) : undefined;
      if (control && value !== undefined) controls[control.id] = value;
    } else if (key.startsWith(FILTER_PREFIX)) {
      const filter = decodeColumnFilter(definition, key.slice(FILTER_PREFIX.length), raw);
      if (filter) filters[filter.columnId] = filter;
    } else if (key === SORT_PARAM) {
      sort = decodeColumnSort(definition, raw);
    } else if (key === DRILL_PARAM) {
      drill = decodeDrill(definition, raw);
    }
  }

  return { controls, sort, filters, drill };
}

/**
 * The query string with this view's params replaced by `state`, leaving every other
 * param — the hosting page's selected item, say — exactly as it was.
 */
export function writeViewUrlState(
  search: string | URLSearchParams,
  state: IViewUrlState,
  definition: IGenericViewDefinitionV2,
): string {
  return mergeViewUrlQuery(search, viewUrlQuery(state, definition));
}

/** Only this view's own params, in definition order — the mirror's change signal. */
export function viewUrlQuery(state: IViewUrlState, definition: IGenericViewDefinitionV2): string {
  const params = new URLSearchParams();
  for (const control of definition.toolbar) {
    const value = state.controls[control.id];
    if (value === undefined || sameControlValue(value, control.defaultValue)) continue;
    // An emptied control with no default carries nothing the reader could not have got
    // by leaving it alone. An empty *array* still does — it means "none", not "all".
    // Text that is only whitespace is the same nothing (I-1298): spaces in Search are no choice.
    if (control.defaultValue === undefined && typeof value === 'string' && !value.trim()) continue;
    const encoded = encodeControlValue(value);
    if (encoded !== null) params.set(`${CONTROL_PREFIX}${control.id}`, encoded);
  }
  if (state.sort) params.set(SORT_PARAM, `${state.sort.columnId}:${state.sort.direction}`);
  for (const filter of Object.values(state.filters)) {
    params.set(`${FILTER_PREFIX}${filter.columnId}`, `${filter.operator}:${filter.value}`);
  }
  if (state.drill && Object.keys(state.drill.coordinates).length > 0) {
    params.set(DRILL_PARAM, encodeDrill(state.drill));
  }
  return params.toString();
}

/** `ownQuery` laid over `search`, dropping whatever this view had written there before. */
export function mergeViewUrlQuery(search: string | URLSearchParams, ownQuery: string): string {
  const params = new URLSearchParams(searchParams(search));
  for (const key of [...params.keys()]) {
    if (isViewUrlParam(key)) params.delete(key);
  }
  for (const [key, value] of new URLSearchParams(ownQuery).entries()) {
    params.append(key, value);
  }
  return params.toString();
}

/** A query string as a location `search` — empty when there is nothing to carry. */
export function asSearchString(query: string): string {
  return query ? `?${query}` : '';
}

/** The query with the open work item set, cleared, or moved to another row. */
export function withSelectedItem(
  search: string | URLSearchParams,
  itemKey: string | null,
  workAreaId?: string | null,
): string {
  const params = new URLSearchParams(searchParams(search));
  params.delete(SELECTED_ITEM_PARAM);
  params.delete(SELECTED_ITEM_AREA_PARAM);
  if (itemKey) {
    params.set(SELECTED_ITEM_PARAM, itemKey);
    if (workAreaId) params.set(SELECTED_ITEM_AREA_PARAM, workAreaId);
  }
  return params.toString();
}

/** Just this view's params, dropping the hosting page's — the mirror's comparison key. */
export function viewUrlParamsOnly(search: string | URLSearchParams): string {
  const params = new URLSearchParams(searchParams(search));
  for (const key of [...params.keys()]) {
    if (!isViewUrlParam(key)) params.delete(key);
  }
  return params.toString();
}

/**
 * Whether a query parameter belongs to a configured view's own reader state.
 *
 * Exported so the unsaved-changes guard can tell a view republishing itself from the reader
 * navigating away — the editor mirrors filters and drills into the address while the author has
 * not moved at all.
 */
export function isViewUrlParam(key: string): boolean {
  return (
    key.startsWith(CONTROL_PREFIX) ||
    key.startsWith(FILTER_PREFIX) ||
    key === SORT_PARAM ||
    key === DRILL_PARAM
  );
}

/** True when both states would serialize to the same query — the anti-bounce guard. */
export function sameViewUrlState(
  left: IViewUrlState,
  right: IViewUrlState,
  definition: IGenericViewDefinitionV2,
): boolean {
  return viewUrlQuery(left, definition) === viewUrlQuery(right, definition);
}

function searchParams(search: string | URLSearchParams): URLSearchParams {
  if (typeof search !== 'string') return search;
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function encodeControlValue(value: GenericViewControlValueType): string | null {
  if (value === null) return null;
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (Array.isArray(value)) return value.map((entry) => encodeURIComponent(entry)).join(',');
  return value;
}

function decodeControlValue(
  control: IGenericViewToolbarControlV2,
  raw: string,
): GenericViewControlValueType | undefined {
  if (control.control === 'toggle') return raw === '1' || raw === 'true';
  if (control.control === 'number') {
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  }
  if (control.control === 'multi-select' || control.control === 'column-picker') {
    return raw === '' ? [] : raw.split(',').map(decodeUriComponentSafe);
  }
  // A range is the pair `from,to` (F-0315); anything else is no range at all.
  if (control.binding.kind === 'range-filter') {
    const range = raw.split(',').map(decodeUriComponentSafe);
    return range.length === 2 ? range : undefined;
  }
  return raw;
}

function sameControlValue(
  value: GenericViewControlValueType,
  other: GenericViewControlValueType | undefined,
): boolean {
  if (other === undefined) return false;
  if (Array.isArray(value) || Array.isArray(other)) {
    return (
      Array.isArray(value) &&
      Array.isArray(other) &&
      value.length === other.length &&
      value.every((entry, index) => entry === other[index])
    );
  }
  return value === other;
}

function decodeColumnSort(
  definition: IGenericViewDefinitionV2,
  raw: string,
): IRuntimeColumnSort | null {
  const separator = raw.lastIndexOf(':');
  if (separator <= 0) return null;
  const direction = raw.slice(separator + 1);
  if (direction !== 'asc' && direction !== 'desc') return null;
  const column = fieldColumn(definition, raw.slice(0, separator));
  if (!column) return null;
  return {
    columnId: column.id,
    datasetId: column.datasetId,
    fieldId: column.fieldId,
    direction,
  };
}

function decodeColumnFilter(
  definition: IGenericViewDefinitionV2,
  columnId: string,
  raw: string,
): IRuntimeColumnFilter | null {
  const separator = raw.indexOf(':');
  if (separator <= 0) return null;
  const operator = raw.slice(0, separator) as DataColumnFilterOperator;
  if (!FILTER_OPERATORS.includes(operator)) return null;
  const column = fieldColumn(definition, columnId);
  if (!column) return null;
  return {
    columnId: column.id,
    datasetId: column.datasetId,
    fieldId: column.fieldId,
    operator,
    value: raw.slice(separator + 1),
  };
}

function encodeDrill(drill: IViewUrlDrill): string {
  const coordinates = Object.entries(drill.coordinates)
    .map(([key, value]) => `${encodeURIComponent(key)}:${encodeURIComponent(value)}`)
    .join(',');
  return `${drill.sourceDatasetId}|${coordinates}|${drill.label}`;
}

function decodeDrill(definition: IGenericViewDefinitionV2, raw: string): IViewUrlDrill | null {
  const datasetEnd = raw.indexOf('|');
  if (datasetEnd <= 0) return null;
  const coordinateEnd = raw.indexOf('|', datasetEnd + 1);
  if (coordinateEnd < 0) return null;
  const sourceDatasetId = raw.slice(0, datasetEnd);
  if (!definition.datasets.some((dataset) => dataset.id === sourceDatasetId)) return null;
  const coordinates = Object.fromEntries(
    raw
      .slice(datasetEnd + 1, coordinateEnd)
      .split(',')
      .flatMap((entry) => {
        const separator = entry.indexOf(':');
        if (separator <= 0) return [];
        return [
          [
            decodeUriComponentSafe(entry.slice(0, separator)),
            decodeUriComponentSafe(entry.slice(separator + 1)),
          ] as const,
        ];
      }),
  );
  if (Object.keys(coordinates).length === 0) return null;
  return { sourceDatasetId, coordinates, label: raw.slice(coordinateEnd + 1) };
}

function fieldColumn(
  definition: IGenericViewDefinitionV2,
  columnId: string,
): Extract<GenericViewColumnV2, { kind: 'field' }> | undefined {
  return definition.columns.find(
    (column): column is Extract<GenericViewColumnV2, { kind: 'field' }> =>
      column.kind === 'field' && column.id === columnId,
  );
}

function decodeUriComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
